import React, { FC } from 'react';

import { isAnnounced, OwnedInvestment as MyInvestment } from 'api-client';
import { CAPITAL_PROTECTION_REQUIRED_PROJECTS } from 'components/banners';
import { CapitalProtectionIcon, CustomLink, Money, ProjectStatusBadge } from 'components/common';
import { ProjectDebtClass } from 'components/project';
import { useProfileData, useProjectRowClick } from 'hooks';
import { RouteDictionary } from 'utils';

import { useMyInvestmentContent } from '../useMyInvestmentContent';
import styles from './MyInvestmentMobileCard.module.scss';

interface Props {
  investment: MyInvestment;
}

const MyInvestmentMobileCard: FC<Props> = ({ investment }) => {
  const { project } = investment;
  const { roiIrr, progressBar, timeline } = useMyInvestmentContent(investment);
  const hasCapitalProtection = investment.investments.some((item) => item.capitalProtection);
  const { capitalProtectedInvestmentsCount } = useProfileData();
  const isCapitalProtectionActive =
    (capitalProtectedInvestmentsCount ?? 0) >= CAPITAL_PROTECTION_REQUIRED_PROJECTS;
  const handleCardClick = useProjectRowClick(project.uuid);

  if (isAnnounced(project.status)) return null;

  return (
    <article className={styles.card} onClick={handleCardClick}>
      <div className={styles.header}>
        <CustomLink
          to={RouteDictionary.DETAILED_PROJECT}
          params={{ uuid: project.uuid }}
          className={styles.projectLink}
        >
          <ProjectDebtClass debtClass={project.debtClass} />
          <div className={styles.projectName}>{project.name[0].text}</div>
          {project.hasExtraCreditorPayment && (
            <div className={styles.projectMeta}>
              <span className={styles.incasso}>инкассо</span>
            </div>
          )}
        </CustomLink>
        <ProjectStatusBadge status={project.status} />
      </div>

      <div className={styles.metrics}>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Инвестировано</span>
          <span className={styles.metricValue}>
            <Money money={investment.investedSum} compact />
            {hasCapitalProtection && (
              <CapitalProtectionIcon
                active={isCapitalProtectionActive}
                className={styles.protectionIcon}
              />
            )}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Прогноз возврата</span>
          <span className={styles.metricValue}>
            {investment.recoveryForecast ? (
              <Money money={investment.recoveryForecast} compact />
            ) : (
              '—'
            )}
          </span>
        </div>
        <div className={styles.metric}>
          <span className={styles.metricLabel}>Прогноз ROI / IRR</span>
          <span className={styles.metricValue}>{roiIrr}</span>
        </div>
      </div>

      <div className={styles.progress}>{progressBar}</div>
      <div className={styles.timeline}>{timeline}</div>
    </article>
  );
};

export default MyInvestmentMobileCard;
